import { DEFAULT_STATUS_GROUP, LEVELS } from '../constants';
import type { ILevel, ILevelConfig, IStatusGroup } from '../types';
import { ELevelState, EProgressStep } from '../types';
import type { TIssuedRewards } from './rewardBonus';

const FULL_PROGRESS = 100;

// below the order of every level, so none of them counts as the current one
const NOT_STARTED_ORDER = 0;

export const getLevelThreshold = (
  { startAmount, targetAmount }: ILevelConfig,
  step: EProgressStep
): number =>
  startAmount + ((targetAmount - startAmount) * step) / FULL_PROGRESS;

const CLEARED_STATES: readonly ELevelState[] = [
  ELevelState.ISSUED,
  ELevelState.CLAIMED,
];

// a cleared level has hit its target, whether the reward is still waiting or not
export const isCleared = ({ state }: ILevel): boolean =>
  CLEARED_STATES.includes(state);

// the bonus outranks the progress: any level whose reward is still waiting reads
// as issued
const getState = (
  order: number,
  currentOrder: number,
  step: EProgressStep,
  isRewardIssued: boolean
): ELevelState => {
  if (isRewardIssued) return ELevelState.ISSUED;
  if (order > currentOrder) return ELevelState.LOCKED;
  if (order === currentOrder && step !== EProgressStep.COMPLETE) {
    return ELevelState.IN_PROGRESS;
  }

  // nothing waiting on a cleared level means the reward was taken; L6-L7 before
  // the pick have no bonus yet, and the selection pop-up covers that moment
  return ELevelState.CLAIMED;
};

// a locked level shows none of it, the one in progress shows how far it got
const getProgress = (state: ELevelState, step: EProgressStep): number => {
  if (state === ELevelState.LOCKED) return 0;

  return state === ELevelState.IN_PROGRESS ? step : FULL_PROGRESS;
};

// one notice at a time, starting from the biggest reward, i.e. the highest level
export const getIssuedLevel = (levels: ILevel[]): ILevel | null =>
  [...levels].reverse().find(({ state }) => state === ELevelState.ISSUED) ??
  null;

export const buildLevels = (
  statusGroup: IStatusGroup | null,
  issuedRewards: TIssuedRewards
): { levels: ILevel[]; currentLevel: ILevel; step: EProgressStep } => {
  const group = statusGroup ?? DEFAULT_STATUS_GROUP;
  const groupIndex = LEVELS.findIndex(({ key }) => key === group.levelKey);

  const isRolledOver =
    group.step === EProgressStep.COMPLETE && groupIndex < LEVELS.length - 1;

  const currentIndex = isRolledOver ? groupIndex + 1 : groupIndex;
  const step = isRolledOver ? EProgressStep.START : group.step;
  // no status group means the pass has not started for this player, a guest
  // included, so every level sits above the current order and reads as locked
  const currentOrder = statusGroup ? currentIndex + 1 : NOT_STARTED_ORDER;

  const levels = LEVELS.map((level) => {
    const state = getState(
      level.order,
      currentOrder,
      step,
      issuedRewards.has(level.order)
    );
    return {
      ...level,
      state,
      progress: getProgress(state, step),
    };
  });

  return { levels, currentLevel: levels[currentIndex], step };
};
